import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { useUpdateCartMutation } from '@/lib/services/carts/carts.query'
import { getErrorMessage } from '@/lib/utils'
import { useState } from 'react'
import { toast } from 'sonner'
import { getPosLocationOptionLabel } from './location-selector.utils'

function buildLocationResetData(locationId) {
  return {
    location_id: locationId,
    fulfillment_type: FulfillmentType.InStore.Value,
    fulfillment_due_date: null,
    special_instructions: null,
    recipient_name: null,
    recipient_phone: null,
    recipient_email: null,
  }
}

export function LocationSelector({
  locations,
  cart,
  defaultLocation,
  selectedLocationId,
  onLocationChange,
  disabled,
  createCartWithDefaults,
}) {
  const updateCartMutation = useUpdateCartMutation()
  const [pendingLocationId, setPendingLocationId] = useState(null)

  const currentLocationId =
    selectedLocationId ?? cart?.location_id ?? cart?.location?.id ?? defaultLocation?.id ?? null
  const shouldConfirmLocationReset =
    Boolean(cart?.id) &&
    [FulfillmentType.Pickup.Value, FulfillmentType.Delivery.Value].includes(
      String(cart?.fulfillment_type ?? '')
    )
  const pendingLocation =
    locations.find((location) => Number(location?.id ?? 0) === Number(pendingLocationId ?? 0)) ??
    null

  const applyLocationChange = async (location, { updateSelectionImmediately = false } = {}) => {
    if (updateSelectionImmediately) {
      onLocationChange?.(location.id)
    }

    let currentCartId = cart?.id

    if (!currentCartId) {
      try {
        await createCartWithDefaults(location)
      } catch (error) {
        toast.error('Failed to create cart', {
          description: getErrorMessage(error) || 'Please try again',
        })
      }

      return
    }

    try {
      await updateCartMutation.mutateAsync({
        cartId: currentCartId,
        data: buildLocationResetData(location.id),
      })
      onLocationChange?.(location.id)
    } catch (error) {
      toast.error('Failed to update cart', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleLocationChange = async (locationId) => {
    const location = locations.find((loc) => loc.id === locationId)
    if (!location || location.id === currentLocationId) return

    if (!cart?.id) {
      await applyLocationChange(location, { updateSelectionImmediately: true })
      return
    }

    if (!shouldConfirmLocationReset) {
      await applyLocationChange(location)
      return
    }

    setPendingLocationId(location.id)
  }

  const handleConfirmLocationChange = async () => {
    if (!pendingLocation) {
      return
    }

    await applyLocationChange(pendingLocation)
    setPendingLocationId(null)
  }

  const handleConfirmationOpenChange = (isOpen) => {
    if (!isOpen) {
      setPendingLocationId(null)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Label htmlFor="location-select" tone="muted">
          Location:
        </Label>
        <Select
          value={currentLocationId?.toString() || ''}
          onValueChange={(value) => handleLocationChange(parseInt(value, 10))}
          disabled={disabled}
        >
          <SelectTrigger id="location-select" className="w-full sm-w-50">
            <SelectValue placeholder="Select location" />
          </SelectTrigger>
          <SelectContent>
            {locations.length === 0 ? (
              <SelectItem value="" disabled>
                No active locations available
              </SelectItem>
            ) : (
              locations.map((location) => (
                <SelectItem key={location.id} value={location.id.toString()}>
                  {getPosLocationOptionLabel(location)}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <ConfirmationDialog
        open={Boolean(pendingLocation)}
        onOpenChange={handleConfirmationOpenChange}
        onConfirm={handleConfirmLocationChange}
        isPending={updateCartMutation.isPending}
        trigger={<span className="hidden" />}
        title="Change location?"
        description="Changing the location will reset this order to in-store and clear the pickup or delivery date, recipient details, special instructions, and delivery address. Do you want to continue?"
        confirmText="Change Location"
        pendingText="Changing Location..."
      />
    </>
  )
}
